import moment from 'moment-timezone'
import type {Ballot, BallotDisplayType} from '../types/ballot'

export type {BallotDisplayType}

export const accessBallotIsPublic = (ballot: Ballot | null | undefined): boolean | undefined => {
  if (!ballot) return
  const ballotIsPublic = ballot?.publish_settings?.privacy?.public
  const anyoneCanView = ballot?.publish_settings?.privacy?.private?.anyone_can_view
  const isMissingPassword = ballot?.publish_settings?.privacy?.private && !(ballot.publish_settings.privacy.private.password)

  return (
    ballotIsPublic
    || anyoneCanView
    || isMissingPassword
  )
}

export const accessBallotDisplayType = (ballot: Ballot | null | undefined): BallotDisplayType | null | undefined => {
  if (!ballot) return null
  const publishSettings = ballot?.publish_settings

  let voterCanVote = false
  switch (publishSettings?.schedule_type?.toLowerCase?.()) {
  case 'manual': {
    const votingIsTurnedOff = publishSettings?.voting_turn_off
    voterCanVote = !votingIsTurnedOff
    break
  }
  case 'schedule': {
    const incomingFormat = 'DD/MM/YYYY HH:mm:ss'
    const outgoingFormat = 'YYYY/MM/DD HH:mm:ss'
    const currentDateTimeAtTimezone = moment.tz(moment(), publishSettings.timezone).format(outgoingFormat)
    const ballotStartDate = moment(publishSettings.start_date, incomingFormat).format(outgoingFormat)
    const ballotEndDate = moment(publishSettings.end_date, incomingFormat).format(outgoingFormat)
    const isWithinScheduledPublishTimes = (
      (ballotStartDate && ballotEndDate)
        ? moment(currentDateTimeAtTimezone).isBetween(ballotStartDate, ballotEndDate)
        : moment(currentDateTimeAtTimezone).isAfter(ballotStartDate)
    )
    voterCanVote = isWithinScheduledPublishTimes
    break
  }
  case 'showresults':
    voterCanVote = false
    break
  default:
    voterCanVote = false
    break
  }
  const ballotType = ballot?.ballot_type?.toLowerCase?.()
  // TODO: Come up with more meaningful return
  if (ballotType !== 'photo' && ballotType !== 'sales') return 'landing-page-only'

  const isPhotoVoting = ballot?.ballot_settings?.state === 'photovoting'
  const isPhotoSubmissions = ballot?.ballot_settings?.state === 'photosubmissions'

  const isShowBallotResults = (publishSettings?.schedule_type === 'showresults') && publishSettings?.show_ballot_results
  const prePostVotingSettings = publishSettings?.pre_post_voting_settings
  const isFullBallotWithVotingTurnedOff = prePostVotingSettings?.display_settings?.full_ballot_with_voting_turned_off

  if (voterCanVote) {
    if (isPhotoSubmissions) return 'photo-submissions-submit'
    if (isPhotoVoting) return 'photo-voting-vote'
    return 'landing-page-only' //Catch-all
  }
  if (!voterCanVote) {
    if (isShowBallotResults) return 'photo-voting-results'
    if (isFullBallotWithVotingTurnedOff) return 'photo-voting-finalist-announcement'
    return 'landing-page-only' //Catch-all
  }
}

export const accessBallotIsPublished = (ballot: Ballot | null | undefined): boolean => {
  return accessBallotDisplayType(ballot) !== 'landing-page-only'
}

const DATE_INCOMING_FORMAT = 'DD/MM/YYYY HH:mm:ss'

export const isBallotScheduledAlreadyEnded = (ballot: Ballot | null | undefined): boolean => {
  if (!ballot) return false
  const publishSettings = ballot.publish_settings
  if (publishSettings?.schedule_type?.toLowerCase?.() !== 'schedule') return false
  if (!publishSettings?.end_date || !publishSettings?.timezone) return false

  const now = moment().tz(publishSettings.timezone)
  const ballotEndDate = moment.tz(publishSettings.end_date, DATE_INCOMING_FORMAT, publishSettings.timezone)
  return now.isAfter(ballotEndDate)
}

export const isBallotScheduledNotStarted = (ballot: Ballot | null | undefined): boolean => {
  if (!ballot) return false
  const publishSettings = ballot.publish_settings
  if (publishSettings?.schedule_type?.toLowerCase?.() !== 'schedule') return false
  if (!publishSettings?.start_date || !publishSettings?.timezone) return false

  const now = moment().tz(publishSettings.timezone)
  const ballotStartDate = moment.tz(publishSettings.start_date, DATE_INCOMING_FORMAT, publishSettings.timezone)
  return now.isBefore(ballotStartDate)
}
